/**
 * L'onboarding: da account appena confermato a Otto pieno dei tuoi dati.
 *
 * Quattro passi, e la barra in alto dice sempre dove sei:
 *
 *  1. **Benvenuto** — cosa succede adesso, e cosa Otto non farà mai da solo;
 *  2. **Guesty** — le due chiavi, con accanto la guida per trovarle. Appena le
 *     incolli Otto prova a leggere gli immobili e te li mostra: «ho trovato
 *     questi» è la conferma che ha funzionato, prima di importare qualunque
 *     cosa;
 *  3. **Importazione** — i passi uno sotto l'altro, che si accendono man mano,
 *     con i numeri che salgono. Lo stato vive sul server: se chiudi la scheda
 *     e torni, riprendi dal punto giusto;
 *  4. **Pronto** — cosa è arrivato, e il tasto per entrare.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BellRing,
  Building2,
  CalendarDays,
  Check,
  CircleAlert,
  ExternalLink,
  KeyRound,
  LogOut,
  MessagesSquare,
  PlugZap,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Ticket,
  Wand2,
} from "lucide-react";
import {
  PASSI_IMPORT,
  type AvanzamentoPasso,
  type OnboardingView,
  type PassoImport,
} from "@otto/shared";
import { api, setToken } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { Pill, Thumb, cx } from "../ui/primitives.tsx";
import { Campo, Logo } from "./Accesso.tsx";

type Passo = "benvenuto" | "guesty" | "importazione" | "pronto";

const PASSI: Array<{ id: Passo; label: string; descrizione: string }> = [
  {
    id: "benvenuto",
    label: "Benvenuto",
    descrizione: "Cosa fa Otto con i tuoi dati",
  },
  {
    id: "guesty",
    label: "Collega Guesty",
    descrizione: "Due chiavi, un minuto",
  },
  {
    id: "importazione",
    label: "Importazione",
    descrizione: "Immobili, calendario, messaggi",
  },
  { id: "pronto", label: "Pronto", descrizione: "Entri e cominci" },
];

/** Ogni quanto rileggere l'avanzamento mentre l'import gira. */
const INTERVALLO_MS = 1_200;

export function Onboarding({
  nome,
  onFinito,
}: {
  nome: string | null;
  onFinito: () => void;
}) {
  const [stato, setStato] = useState<OnboardingView | null>(null);
  const [passo, setPasso] = useState<Passo | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const leggi = useCallback(async () => {
    const { onboarding } = await api.onboarding();
    setStato(onboarding);
    return onboarding;
  }, []);

  // All'apertura si decide da dove ripartire: chi torna a import avviato
  // ritrova la schermata di caricamento, non il benvenuto.
  useEffect(() => {
    void leggi()
      .then((o) => {
        if (o.passo === "fatto") onFinito();
        else if (o.import.stato === "completato") setPasso("pronto");
        else if (o.import.stato) setPasso("importazione");
        else setPasso("benvenuto");
      })
      .catch((e: unknown) => setErrore(messaggioDi(e)));
  }, [leggi, onFinito]);

  const esci = () => {
    setToken(null);
    window.location.reload();
  };

  return (
    <div className="flex h-full overflow-hidden bg-surface">
      {/* ── I passi, in colonna ────────────────────────────────────────── */}
      <aside className="hidden w-[320px] shrink-0 flex-col border-r border-line bg-surface-muted px-8 py-8 lg:flex">
        <div className="flex items-center gap-2">
          <Logo />
          <span className="text-[19px] font-extrabold tracking-tight text-ink">
            Otto
          </span>
        </div>
        <div className="mt-14">
          <Stepper attivo={passo} />
        </div>
        <div className="mt-auto flex flex-col gap-4">
          <p className="text-[12.5px] leading-relaxed text-ink-faint">
            Serve aiuto? Scrivici e ti colleghiamo noi.
          </p>
          <button
            type="button"
            onClick={esci}
            className="flex w-fit items-center gap-1.5 text-[13px] font-semibold text-ink-soft transition-colors duration-quick hover:text-ink"
          >
            <LogOut size={14} />
            Esci
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Su schermo stretto la colonna non c'è: una riga in cima al suo posto. */}
        <header className="flex items-center gap-3 border-b border-line px-5 py-4 lg:hidden">
          <Logo />
          <span className="text-[13px] font-semibold text-ink-soft">
            {passo
              ? `Passo ${PASSI.findIndex((p) => p.id === passo) + 1} di ${PASSI.length}`
              : ""}
          </span>
          <button
            type="button"
            onClick={esci}
            aria-label="Esci"
            className="ml-auto text-ink-soft hover:text-ink"
          >
            <LogOut size={16} />
          </button>
        </header>

        <main className="scroll-soft min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex min-h-full w-full max-w-[1120px] flex-col justify-center px-6 py-12 sm:px-12 xl:px-16">
            {errore ? (
              <p className="rounded-card bg-danger-soft px-4 py-3 text-[14px] font-semibold text-danger">
                {errore}
              </p>
            ) : !stato || !passo ? (
              <div className="grid place-items-center py-24">
                <span className="size-8 animate-spin rounded-full border-[3px] border-line border-t-ink" />
              </div>
            ) : (
              <div key={passo} className="animate-rise">
                {passo === "benvenuto" ? (
                  <Benvenuto nome={nome} onAvanti={() => setPasso("guesty")} />
                ) : passo === "guesty" ? (
                  <Guesty
                    onImportato={async () => {
                      const { onboarding } = await api.avviaImport();
                      setStato(onboarding);
                      setPasso("importazione");
                    }}
                  />
                ) : passo === "importazione" ? (
                  <Importazione
                    stato={stato}
                    leggi={leggi}
                    onFinito={() => setPasso("pronto")}
                    onCambiaChiavi={() => setPasso("guesty")}
                    onRiprova={async () => {
                      const { onboarding } = await api.avviaImport();
                      setStato(onboarding);
                    }}
                  />
                ) : (
                  <Pronto
                    stato={stato}
                    onEntra={async () => {
                      await api.completaOnboarding();
                      onFinito();
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// ── I passi, in verticale ────────────────────────────────────────────────────

function Stepper({ attivo }: { attivo: Passo | null }) {
  const indice = PASSI.findIndex((p) => p.id === attivo);
  return (
    <ol className="flex flex-col">
      {PASSI.map((p, i) => {
        const fatto = i < indice;
        const corrente = i === indice;
        return (
          <li key={p.id} className="flex gap-3.5">
            <div className="flex flex-col items-center">
              <span
                className={cx(
                  "grid size-8 shrink-0 place-items-center rounded-full text-[12.5px] font-bold transition-all duration-calm",
                  fatto && "bg-ink text-ink-inverse",
                  corrente && "bg-surface text-ink ring-2 ring-ink",
                  !fatto &&
                    !corrente &&
                    "bg-surface text-ink-faint ring-1 ring-line",
                )}
              >
                {fatto ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              {i < PASSI.length - 1 ? (
                <span
                  className={cx(
                    "my-1.5 w-px flex-1 transition-colors duration-calm",
                    fatto ? "bg-ink" : "bg-line-strong",
                  )}
                />
              ) : null}
            </div>
            <div className={cx("pt-1", i < PASSI.length - 1 && "pb-8")}>
              <p
                className={cx(
                  "text-[14px] font-bold",
                  corrente || fatto ? "text-ink" : "text-ink-faint",
                )}
              >
                {p.label}
              </p>
              <p className="mt-0.5 text-[12.5px] leading-snug text-ink-faint">
                {p.descrizione}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ── 1. Benvenuto ─────────────────────────────────────────────────────────────

function Benvenuto({
  nome,
  onAvanti,
}: {
  nome: string | null;
  onAvanti: () => void;
}) {
  const cose = [
    {
      Icona: Building2,
      titolo: "Legge da Guesty",
      testo: "Immobili, calendario, prenotazioni e messaggi degli ospiti.",
    },
    {
      Icona: BellRing,
      titolo: "Ti avvisa",
      testo:
        "Arrivi senza pulizia, notti vuote, ospiti che aspettano una risposta.",
    },
    {
      Icona: ShieldCheck,
      titolo: "Non cambia niente da solo",
      testo:
        "Ogni modifica su Guesty te la mostra prima. Parte solo se approvi.",
    },
  ];

  const serve = [
    "Un accesso da amministratore a Guesty",
    "Due minuti per creare le chiavi",
    "Niente da installare, niente carta di credito",
  ];

  return (
    <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-pill bg-ok-soft px-3 py-1 text-[12.5px] font-semibold text-ok">
          <Check size={13} strokeWidth={3} />
          Account creato
        </span>
        <h1 className="mt-5 text-[40px] font-extrabold leading-[1.06] tracking-tight text-ink xl:text-[46px]">
          {nome ? `Ciao ${nome}.` : "Ciao."}
          <br />
          <span className="text-ink-soft">Colleghiamo i tuoi immobili.</span>
        </h1>
        <p className="mt-5 max-w-[480px] text-[16px] leading-relaxed text-ink-soft">
          Incolli due chiavi di Guesty e Otto legge immobili, calendario e
          messaggi. Da lì in poi si tiene aggiornato da solo.
        </p>

        <div className="mt-8 max-w-[440px] rounded-card-lg border border-line p-5">
          <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            Ti serve
          </p>
          <ul className="mt-3 flex flex-col gap-2.5">
            {serve.map((cosa) => (
              <li
                key={cosa}
                className="flex items-center gap-2.5 text-[14px] text-ink"
              >
                <span className="grid size-5 shrink-0 place-items-center rounded-full bg-ink text-ink-inverse">
                  <Check size={11} strokeWidth={3} />
                </span>
                {cosa}
              </li>
            ))}
          </ul>
        </div>

        <Pill
          variant="primary"
          size="lg"
          icon={<ArrowRight size={16} />}
          onClick={onAvanti}
          className="mt-8"
        >
          Iniziamo
        </Pill>
      </div>

      <ul className="flex flex-col gap-3">
        {cose.map(({ Icona, titolo, testo }, i) => (
          <li
            key={titolo}
            className="animate-rise flex gap-4 rounded-card-lg border border-line bg-surface p-5 shadow-soft"
            style={{ animationDelay: `${80 + i * 90}ms` }}
          >
            <span className="grid size-11 shrink-0 place-items-center rounded-card bg-surface-muted text-ink">
              <Icona size={20} />
            </span>
            <span>
              <span className="block text-[16px] font-bold text-ink">
                {titolo}
              </span>
              <span className="mt-1 block text-[14px] leading-relaxed text-ink-soft">
                {testo}
              </span>
            </span>
          </li>
        ))}
        <li className="flex items-center gap-3 rounded-card-lg bg-surface-muted px-5 py-4 text-[13.5px] leading-relaxed text-ink-soft">
          <Sparkles size={17} className="shrink-0 text-ink" />
          Poi gli chiedi quello che vuoi in chat: «chi arriva domani?», «quanto
          ho guadagnato ad agosto?», «alza i prezzi del weekend».
        </li>
      </ul>
    </div>
  );
}

// ── 2. Le chiavi di Guesty ───────────────────────────────────────────────────

type Anteprima = Awaited<ReturnType<typeof api.collegaGestionale>>;

function Guesty({ onImportato }: { onImportato: () => Promise<void> }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [provando, setProvando] = useState(false);
  const [avviando, setAvviando] = useState(false);
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null);

  const prova = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrore(null);
    setProvando(true);
    try {
      setAnteprima(
        await api.collegaGestionale(clientId.trim(), clientSecret.trim()),
      );
    } catch (e) {
      setErrore(messaggioDi(e));
    } finally {
      setProvando(false);
    }
  };

  const importa = async () => {
    setAvviando(true);
    try {
      await onImportato();
    } catch (e) {
      setErrore(messaggioDi(e));
      setAvviando(false);
    }
  };

  return (
    <div>
      {/* In cima: cosa si fa, e le tre cose che rassicurano */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="grid size-12 place-items-center rounded-card-lg bg-surface-muted text-ink">
            <PlugZap size={22} />
          </span>
          <h1 className="mt-5 text-[34px] font-extrabold leading-tight tracking-tight text-ink">
            Collega Guesty
          </h1>
          <p className="mt-2 max-w-[560px] text-[15.5px] leading-relaxed text-ink-soft">
            Crea un'applicazione su Guesty e incolla qui le sue due chiavi. Non
            devi esportare niente: i dati li legge Otto.
          </p>
        </div>
        <ul className="flex flex-wrap gap-2 lg:justify-end">
          {[
            { Icona: ShieldCheck, testo: "Chiavi cifrate" },
            { Icona: Check, testo: "Nessuna modifica senza il tuo ok" },
            { Icona: RefreshCw, testo: "Revocabili da Guesty" },
          ].map(({ Icona, testo }) => (
            <li
              key={testo}
              className="flex items-center gap-1.5 rounded-pill bg-surface-muted px-3 py-1.5 text-[12.5px] font-semibold text-ink-soft"
            >
              <Icona size={13} className="text-ink" />
              {testo}
            </li>
          ))}
        </ul>
      </div>

      {anteprima ? (
        <TrovatiImmobili
          anteprima={anteprima}
          avviando={avviando}
          errore={errore}
          onImporta={importa}
          onCambia={() => setAnteprima(null)}
        />
      ) : (
        <>
          <div className="mt-9 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
            <form
              onSubmit={prova}
              className="flex flex-col gap-5 rounded-card-lg border border-line p-7 shadow-soft"
            >
              <div>
                <p className="text-[16px] font-extrabold text-ink">
                  Le tue chiavi
                </p>
                <p className="mt-0.5 text-[13.5px] text-ink-soft">
                  Le trovi nell'applicazione che crei su Guesty, qui accanto
                  come.
                </p>
              </div>
              <Campo
                id="client-id"
                label="Client ID"
                icona={<Ticket size={15} />}
                value={clientId}
                onChange={setClientId}
                placeholder="0oa…"
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
              <Campo
                id="client-secret"
                label="Client Secret"
                icona={<KeyRound size={15} />}
                value={clientSecret}
                onChange={setClientSecret}
                type="password"
                autoComplete="off"
                spellCheck={false}
              />
              {errore ? (
                <p
                  role="alert"
                  className="animate-rise flex gap-2 rounded-card bg-danger-soft px-3.5 py-3 text-[13.5px] font-semibold leading-snug text-danger"
                >
                  <CircleAlert size={16} className="mt-px shrink-0" />
                  {errore}
                </p>
              ) : null}
              <div className="flex flex-col gap-3 pt-1">
                <Pill
                  variant="primary"
                  size="lg"
                  type="submit"
                  loading={provando}
                  icon={<ArrowRight size={16} />}
                  className="self-start"
                >
                  {provando ? "Provo a collegarmi…" : "Collega e controlla"}
                </Pill>
              </div>
              <p className="mt-auto border-t border-line pt-4 text-[12.5px] leading-relaxed text-ink-faint">
                Guesty permette cinque connessioni al giorno per ogni coppia di
                chiavi. La prova ne usa una, e l'importazione riusa la stessa.
              </p>
            </form>

            <Guida />
          </div>

          <CosaLegge />
        </>
      )}
    </div>
  );
}

/** La fascia in fondo: cosa entra in Otto, cosa no. */
function CosaLegge() {
  const voci = [
    {
      Icona: Building2,
      titolo: "Cosa legge",
      testo:
        "Immobili, calendario e prezzi, prenotazioni, messaggi degli ospiti.",
    },
    {
      Icona: ShieldCheck,
      titolo: "Cosa non fa mai da solo",
      testo:
        "Cambiare un prezzo o rispondere a un ospite: te lo propone, e parte solo se approvi.",
    },
    {
      Icona: RefreshCw,
      titolo: "Se cambi idea",
      testo:
        "Elimini l'applicazione su Guesty e Otto non legge più niente. I tuoi dati li scarichi dalle impostazioni.",
    },
  ];
  return (
    <ul className="mt-5 grid gap-3 sm:grid-cols-3">
      {voci.map(({ Icona, titolo, testo }) => (
        <li key={titolo} className="rounded-card-lg bg-surface-muted p-5">
          <Icona size={17} className="text-ink" />
          <p className="mt-3 text-[14px] font-bold text-ink">{titolo}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
            {testo}
          </p>
        </li>
      ))}
    </ul>
  );
}

function TrovatiImmobili({
  anteprima,
  avviando,
  errore,
  onImporta,
  onCambia,
}: {
  anteprima: Anteprima;
  avviando: boolean;
  errore: string | null;
  onImporta: () => void;
  onCambia: () => void;
}) {
  const altri = anteprima.immobili - anteprima.anteprima.length;
  return (
    <div className="animate-rise mt-9 rounded-card-lg border border-line p-7 shadow-soft">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2.5 text-[17px] font-extrabold text-ink">
          <span className="grid size-7 place-items-center rounded-full bg-ok text-ink-inverse">
            <Check size={15} strokeWidth={3} />
          </span>
          Collegato. Ho trovato{" "}
          {anteprima.immobili === 1
            ? "1 immobile"
            : `${anteprima.immobili} immobili`}
          .
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Pill
            variant="ghost"
            size="md"
            onClick={onCambia}
            disabled={avviando}
          >
            Non sono questi: cambia chiavi
          </Pill>
          <Pill
            variant="primary"
            size="lg"
            icon={<Wand2 size={16} />}
            loading={avviando}
            onClick={onImporta}
          >
            Importa tutto
          </Pill>
        </div>
      </div>

      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {anteprima.anteprima.map((immobile, i) => (
          <li
            key={`${immobile.nome}-${i}`}
            className="animate-rise flex items-center gap-3 rounded-card border border-line px-3 py-2.5"
            style={{ animationDelay: `${i * 45}ms` }}
          >
            <Thumb src={immobile.foto} alt={immobile.nome} size={40} />
            <span className="min-w-0">
              <span className="block truncate text-[13.5px] font-bold text-ink">
                {immobile.nome}
              </span>
              <span className="block truncate text-[12px] text-ink-faint">
                {immobile.citta ?? "—"}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {altri > 0 ? (
        <p className="mt-3 text-[13px] text-ink-faint">e altri {altri}.</p>
      ) : null}
      {anteprima.disattivati ? (
        <p className="mt-2 text-[13px] text-ink-soft">
          {anteprima.disattivati === 1
            ? "Uno è disattivato su Guesty: quello non lo porto dentro."
            : `${anteprima.disattivati} sono disattivati su Guesty: quelli non li porto dentro.`}
        </p>
      ) : null}
      {errore ? (
        <p
          role="alert"
          className="mt-4 flex gap-2 rounded-card bg-danger-soft px-3.5 py-3 text-[13.5px] font-semibold leading-snug text-danger"
        >
          <CircleAlert size={16} className="mt-px shrink-0" />
          {errore}
        </p>
      ) : null}
      <p className="mt-6 border-t border-line pt-4 text-[13px] leading-relaxed text-ink-soft">
        Importo immobili, prenotazioni, calendario e messaggi. Ci vuole qualche
        minuto, e puoi chiudere la pagina: quando torni lo ritrovi qui.
      </p>
    </div>
  );
}

function Guida() {
  const passi = [
    {
      titolo: "Entra in Guesty",
      testo: (
        <>
          Con un utente <b>amministratore</b>: gli altri non vedono le
          applicazioni.
        </>
      ),
    },
    {
      titolo: "Apri le applicazioni",
      testo: (
        <>
          <b>Integrations → Developer tools → OAuth applications</b>.
        </>
      ),
    },
    {
      titolo: "Creane una nuova",
      testo: (
        <>
          Premi <b>New application</b>, chiamala «Otto» e salva.
        </>
      ),
    },
    {
      titolo: "Copia le due chiavi",
      testo: (
        <>
          <b>Client ID</b> e <b>Client Secret</b>, e incollale qui accanto.
        </>
      ),
    },
  ];
  return (
    <aside className="flex flex-col rounded-card-lg border border-line bg-surface-muted p-7">
      <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint">
        Dove le trovo
      </p>
      <ol className="mt-5 flex flex-col">
        {passi.map(({ titolo, testo }, i) => (
          <li key={titolo} className="flex gap-3.5">
            <div className="flex flex-col items-center">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-surface text-[12.5px] font-bold text-ink ring-1 ring-line">
                {i + 1}
              </span>
              {i < passi.length - 1 ? (
                <span className="my-1 w-px flex-1 bg-line-strong" />
              ) : null}
            </div>
            <div className={cx("pt-0.5", i < passi.length - 1 && "pb-5")}>
              <p className="text-[14px] font-bold text-ink">{titolo}</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
                {testo}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-2 flex gap-2 rounded-field bg-warn-soft px-3 py-2.5 text-[12.5px] leading-relaxed text-warn">
        <CircleAlert size={15} className="mt-px shrink-0" />
        Guesty mostra il Client Secret una volta sola: copialo subito. Se l'hai
        perso, crea una nuova applicazione.
      </p>
      <a
        href="https://open-api-docs.guesty.com/docs/quick-start-guide"
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink underline-offset-2 hover:underline"
      >
        La guida di Guesty
        <ExternalLink size={13} />
      </a>
    </aside>
  );
}

// ── 3. L'importazione ────────────────────────────────────────────────────────

const PASSI_VISTI: Record<
  PassoImport,
  { titolo: string; Icona: typeof Building2; unita?: string }
> = {
  collegamento: { titolo: "Collegamento a Guesty", Icona: PlugZap },
  immobili: { titolo: "Immobili", Icona: Building2, unita: "immobili" },
  prenotazioni: {
    titolo: "Prenotazioni",
    Icona: Ticket,
    unita: "prenotazioni",
  },
  calendario: {
    titolo: "Calendario e prezzi",
    Icona: CalendarDays,
    unita: "immobili",
  },
  conversazioni: {
    titolo: "Messaggi degli ospiti",
    Icona: MessagesSquare,
    unita: "conversazioni",
  },
  analisi: { titolo: "Controlli e avvisi", Icona: Sparkles, unita: "avvisi" },
};

/** La frase sotto la barra: dice cosa sta succedendo adesso. */
const FRASE_PER_PASSO: Record<PassoImport, string> = {
  collegamento: "Mi collego a Guesty…",
  immobili: "Leggo gli immobili e le loro foto…",
  prenotazioni: "Leggo le prenotazioni…",
  calendario: "Controllo prezzi e disponibilità notte per notte…",
  conversazioni: "Leggo i messaggi degli ospiti…",
  analisi: "Cerco quello che non va…",
};

function Importazione({
  stato,
  leggi,
  onFinito,
  onCambiaChiavi,
  onRiprova,
}: {
  stato: OnboardingView;
  leggi: () => Promise<OnboardingView>;
  onFinito: () => void;
  onCambiaChiavi: () => void;
  onRiprova: () => Promise<void>;
}) {
  const [riprovando, setRiprovando] = useState(false);
  const inCorso = stato.import.stato === "in_corso";
  const fallito = stato.import.stato === "fallito";
  const finitoRef = useRef(false);

  // Si rilegge finché l'import gira. Il server è la verità: se la scheda si
  // chiude, lo stato non si perde.
  useEffect(() => {
    if (!inCorso) return;
    const timer = setInterval(
      () => void leggi().catch(() => {}),
      INTERVALLO_MS,
    );
    return () => clearInterval(timer);
  }, [inCorso, leggi]);

  // Finito: un attimo per vedere l'ultima spunta accendersi, poi il riepilogo.
  useEffect(() => {
    if (stato.import.stato !== "completato" || finitoRef.current) return;
    finitoRef.current = true;
    const t = setTimeout(onFinito, 900);
    return () => clearTimeout(t);
  }, [stato.import.stato, onFinito]);

  const avanzamento = stato.import.avanzamento;
  const quota = percentuale(avanzamento);

  const corrente = PASSI_IMPORT.find(
    (id) => avanzamento?.[id]?.stato === "in_corso",
  );

  return (
    <div className="grid items-start gap-10 lg:grid-cols-[1.25fr_1fr] lg:gap-14">
      <div>
        <h1 className="text-[34px] font-extrabold leading-tight tracking-tight text-ink">
          {fallito
            ? "L'importazione si è fermata"
            : stato.import.stato === "completato"
              ? "Fatto"
              : "Sto importando i tuoi dati"}
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-soft">
          {fallito
            ? "Non è colpa tua: qui sotto c'è il motivo, e puoi riprovare."
            : "Puoi chiudere la pagina: l'importazione continua, e quando torni la ritrovi qui."}
        </p>

        {/* La barra: si riempie sui passi fatti, e il calendario conta per quanto pesa. */}
        <div className="mt-8">
          <div className="h-2 overflow-hidden rounded-pill bg-surface-sunken">
            <div
              className={cx(
                "relative h-full rounded-pill transition-[width] duration-700 ease-out",
                fallito ? "bg-danger" : "bg-ink",
              )}
              style={{ width: `${Math.max(4, quota)}%` }}
            >
              {inCorso ? (
                <span className="absolute inset-0 animate-pulse rounded-pill bg-white/15" />
              ) : null}
            </div>
          </div>
          <div className="mt-2 flex justify-between text-[12.5px] text-ink-faint">
            <span key={corrente ?? "fermo"} className="animate-fade">
              {inCorso
                ? FRASE_PER_PASSO[corrente ?? "collegamento"]
                : fallito
                  ? "Fermo"
                  : "Completato"}
            </span>
            <span className="font-bold tabular-nums text-ink">{quota}%</span>
          </div>
        </div>

        <ol className="mt-8 flex flex-col">
          {PASSI_IMPORT.map((id, i) => (
            <RigaPasso
              key={id}
              id={id}
              valore={avanzamento?.[id] ?? { stato: "da_fare" }}
              ultimo={i === PASSI_IMPORT.length - 1}
            />
          ))}
        </ol>

        {fallito ? (
          <div className="animate-rise mt-6 rounded-card-lg border border-danger/25 bg-danger-soft p-5">
            <p className="flex gap-2 text-[14px] font-semibold leading-snug text-danger">
              <CircleAlert size={17} className="mt-px shrink-0" />
              {stato.import.errore ?? "Motivo sconosciuto."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <Pill
                variant="primary"
                size="md"
                icon={<RefreshCw size={15} />}
                loading={riprovando}
                onClick={async () => {
                  setRiprovando(true);
                  try {
                    await onRiprova();
                  } finally {
                    setRiprovando(false);
                  }
                }}
              >
                Riprova
              </Pill>
              <Pill variant="secondary" size="md" onClick={onCambiaChiavi}>
                Cambia le chiavi
              </Pill>
            </div>
          </div>
        ) : null}
      </div>

      <MentreAspetti avanzamento={avanzamento} />
    </div>
  );
}

/** La colonna di destra: cosa è arrivato, e cosa potrai chiedere. */
function MentreAspetti({
  avanzamento,
}: {
  avanzamento: OnboardingView["import"]["avanzamento"];
}) {
  const numeri = [
    {
      Icona: Building2,
      label: "immobili",
      valore: avanzamento?.immobili.fatti,
    },
    {
      Icona: Ticket,
      label: "prenotazioni",
      valore: avanzamento?.prenotazioni.fatti,
    },
    {
      Icona: CalendarDays,
      label: "calendari letti",
      valore: avanzamento?.calendario.fatti,
    },
    {
      Icona: MessagesSquare,
      label: "conversazioni",
      valore: avanzamento?.conversazioni.fatti,
    },
  ];
  const domande = [
    "Chi arriva questa settimana?",
    "Quanto ho guadagnato ad agosto?",
    "Alza del 10% i prezzi del prossimo weekend",
    "Rispondi a chi aspetta da ieri",
  ];
  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-0">
      <div className="rounded-card-lg border border-line p-5">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint">
          Arrivato finora
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {numeri.map(({ Icona, label, valore }) => (
            <div key={label} className="rounded-card bg-surface-muted p-3.5">
              <Icona size={16} className="text-ink-faint" />
              <p className="mt-2 text-[24px] font-extrabold leading-none tabular-nums text-ink">
                {valore != null ? <Contatore a={valore} /> : "—"}
              </p>
              <p className="mt-1 text-[12.5px] text-ink-soft">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-card-lg border border-line p-5">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint">
          Tra poco potrai chiedere
        </p>
        <ul className="mt-3 flex flex-col gap-2">
          {domande.map((d) => (
            <li
              key={d}
              className="flex items-center gap-2.5 rounded-field bg-surface-muted px-3.5 py-2.5 text-[13.5px] text-ink"
            >
              <MessagesSquare size={14} className="shrink-0 text-ink-faint" />
              {d}
            </li>
          ))}
        </ul>
      </div>

      <p className="flex gap-2.5 px-1 text-[13px] leading-relaxed text-ink-soft">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-ink" />
        Sto solo leggendo: su Guesty non cambia niente.
      </p>
    </aside>
  );
}

function RigaPasso({
  id,
  valore,
  ultimo,
}: {
  id: PassoImport;
  valore: AvanzamentoPasso;
  ultimo: boolean;
}) {
  const visto = PASSI_VISTI[id];
  const { stato } = valore;
  const conteggio =
    valore.fatti != null
      ? valore.totale != null && stato === "in_corso"
        ? `${valore.fatti} di ${valore.totale}`
        : `${valore.fatti} ${visto.unita ?? ""}`.trim()
      : null;

  return (
    <li className="flex gap-4">
      {/* Il pallino e il filo che lo lega al prossimo. */}
      <div className="flex flex-col items-center">
        <span
          className={cx(
            "grid size-9 shrink-0 place-items-center rounded-full transition-all duration-calm",
            stato === "fatto" && "animate-pop bg-ink text-ink-inverse",
            stato === "in_corso" && "bg-surface text-ink ring-2 ring-ink",
            stato === "saltato" && "bg-warn-soft text-warn",
            stato === "da_fare" && "bg-surface-muted text-ink-faint",
          )}
        >
          {stato === "fatto" ? (
            <Check size={16} strokeWidth={3} />
          ) : stato === "in_corso" ? (
            <span className="size-4 animate-spin rounded-full border-2 border-line border-t-ink" />
          ) : stato === "saltato" ? (
            <CircleAlert size={16} />
          ) : (
            <visto.Icona size={16} />
          )}
        </span>
        {!ultimo ? (
          <span
            className={cx(
              "my-1 w-px flex-1 transition-colors duration-calm",
              stato === "fatto" ? "bg-ink" : "bg-line",
            )}
          />
        ) : null}
      </div>

      <div className={cx("min-w-0 flex-1", ultimo ? "pb-0" : "pb-5")}>
        <div className="flex items-baseline justify-between gap-3 pt-1.5">
          <p
            className={cx(
              "text-[15px] font-bold transition-colors duration-calm",
              stato === "da_fare" ? "text-ink-faint" : "text-ink",
            )}
          >
            {visto.titolo}
          </p>
          {conteggio ? (
            <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">
              {conteggio}
            </span>
          ) : null}
        </div>
        {stato === "in_corso" && valore.totale ? (
          <div className="mt-2 h-1 overflow-hidden rounded-pill bg-surface-sunken">
            <div
              className="h-full rounded-pill bg-ink transition-[width] duration-500"
              style={{
                width: `${Math.round(((valore.fatti ?? 0) / Math.max(1, valore.totale)) * 100)}%`,
              }}
            />
          </div>
        ) : null}
        {valore.nota ? (
          <p
            className={cx(
              "mt-1 text-[12.5px] leading-snug",
              stato === "saltato" ? "text-warn" : "text-ink-faint",
            )}
          >
            {valore.nota}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/** Quanto è fatto, pesando il calendario per quello che dura davvero. */
function percentuale(
  avanzamento: OnboardingView["import"]["avanzamento"],
): number {
  if (!avanzamento) return 0;
  const pesi: Record<PassoImport, number> = {
    collegamento: 5,
    immobili: 10,
    prenotazioni: 10,
    calendario: 45,
    conversazioni: 20,
    analisi: 10,
  };
  let fatto = 0;
  for (const id of PASSI_IMPORT) {
    const passo = avanzamento[id];
    const peso = pesi[id];
    if (passo.stato === "fatto" || passo.stato === "saltato") fatto += peso;
    else if (passo.stato === "in_corso") {
      const parte =
        passo.totale && passo.totale > 0
          ? (passo.fatti ?? 0) / passo.totale
          : 0.3;
      fatto += peso * Math.min(1, parte);
    }
  }
  return Math.min(100, Math.round(fatto));
}

// ── 4. Pronto ────────────────────────────────────────────────────────────────

function Pronto({
  stato,
  onEntra,
}: {
  stato: OnboardingView;
  onEntra: () => Promise<void>;
}) {
  const [entrando, setEntrando] = useState(false);
  const r = stato.riepilogo;
  const numeri = r
    ? [
        { valore: r.immobili, label: "immobili", Icona: Building2 },
        { valore: r.prenotazioni, label: "prenotazioni", Icona: Ticket },
        { valore: r.notti, label: "notti in calendario", Icona: CalendarDays },
        {
          valore: r.conversazioni,
          label: "conversazioni",
          Icona: MessagesSquare,
        },
      ]
    : [];

  const entra = async (ancora: string) => {
    setEntrando(true);
    try {
      window.location.hash = ancora;
      await onEntra();
    } catch {
      setEntrando(false);
    }
  };
  const avvisi = r?.avvisi ?? 0;
  const partenze = [
    {
      ancora: "avvisi",
      Icona: BellRing,
      titolo:
        avvisi > 0
          ? `${avvisi === 1 ? "1 avviso" : `${avvisi} avvisi`} da guardare`
          : "Gli avvisi",
      testo:
        avvisi > 0
          ? "Arrivi senza pulizia, prezzi sotto il minimo, ospiti che aspettano. Ognuno con il suo tasto per sistemarlo."
          : "Per ora niente da segnalare. Quando qualcosa non va, lo trovi qui con il tasto per sistemarlo.",
      evidenza: avvisi > 0,
    },
    {
      ancora: "chat",
      Icona: MessagesSquare,
      titolo: "Chiedi a Otto",
      testo:
        "«Chi arriva questa settimana?», «alza i prezzi del weekend». Prepara lui la modifica, tu approvi.",
      evidenza: false,
    },
    {
      ancora: "impostazioni",
      Icona: ShieldCheck,
      titolo: "Decidi i limiti",
      testo:
        "Quante modifiche al giorno, il regime fiscale, cosa Otto può fare senza chiederti.",
      evidenza: false,
    },
  ];

  return (
    <div className="mx-auto max-w-[820px]">
      <div className="text-center">
        <span className="animate-pop mx-auto grid size-16 place-items-center rounded-full bg-ink text-ink-inverse">
          <Check size={30} strokeWidth={3} />
        </span>
        <h1 className="mt-6 text-[38px] font-extrabold leading-tight tracking-tight text-ink">
          Tutto pronto
        </h1>
        <p className="mx-auto mt-2 max-w-[500px] text-[15.5px] leading-relaxed text-ink-soft">
          {stato.nomeAttivita} è collegato. Otto lo tiene aggiornato da solo, e
          intanto recupera lo storico degli ultimi due anni.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {numeri.map(({ valore, label, Icona }, i) => (
          <div
            key={label}
            className="animate-rise rounded-card-lg border border-line p-5"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <Icona size={17} className="text-ink-faint" />
            <p className="mt-3 text-[30px] font-extrabold leading-none tabular-nums text-ink">
              <Contatore a={valore} />
            </p>
            <p className="mt-1.5 text-[13px] text-ink-soft">{label}</p>
          </div>
        ))}
      </div>

      <p className="mt-10 text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint">
        Da dove partire
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {partenze.map(({ ancora, Icona, titolo, testo, evidenza }, i) => (
          <button
            key={ancora}
            type="button"
            disabled={entrando}
            onClick={() => void entra(ancora)}
            className={cx(
              "animate-rise group flex flex-col rounded-card-lg border p-5 text-left transition-all duration-quick hover:-translate-y-0.5 hover:shadow-lift disabled:opacity-60",
              evidenza
                ? "border-warn/40 bg-warn-soft/60"
                : "border-line hover:border-line-strong",
            )}
            style={{ animationDelay: `${320 + i * 80}ms` }}
          >
            <span
              className={cx(
                "grid size-9 place-items-center rounded-full",
                evidenza
                  ? "bg-warn text-ink-inverse"
                  : "bg-surface-muted text-ink",
              )}
            >
              <Icona size={16} />
            </span>
            <p className="mt-4 text-[15px] font-bold text-ink">{titolo}</p>
            <p className="mt-1 flex-1 text-[13px] leading-relaxed text-ink-soft">
              {testo}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-ink">
              Apri
              <ArrowRight
                size={14}
                className="transition-transform duration-quick group-hover:translate-x-0.5"
              />
            </span>
          </button>
        ))}
      </div>

      <div className="mt-10 flex flex-col items-center gap-3">
        <Pill
          variant="primary"
          size="lg"
          icon={<ArrowRight size={16} />}
          loading={entrando}
          onClick={() => void entra("chat")}
        >
          Entra in Otto
        </Pill>
        <p className="text-[12.5px] text-ink-faint">
          Niente cambia su Guesty senza il tuo ok.
        </p>
      </div>
    </div>
  );
}

/** Un numero che sale fino al suo valore: il riepilogo si legge meglio così. */
function Contatore({ a }: { a: number }) {
  const [valore, setValore] = useState(0);
  // Si parte da dove si era: durante l'import il numero cresce a scatti, e
  // ripartire da zero a ogni scatto lo terrebbe sempre indietro.
  const ultimo = useRef(0);
  useEffect(() => {
    const da = ultimo.current;
    const durata = 900;
    const inizio = performance.now();
    let frame = 0;
    const passo = (ora: number) => {
      const t = Math.min(1, (ora - inizio) / durata);
      const morbido = 1 - Math.pow(1 - t, 3);
      const adesso = Math.round(da + (a - da) * morbido);
      ultimo.current = adesso;
      setValore(adesso);
      if (t < 1) frame = requestAnimationFrame(passo);
    };
    frame = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(frame);
  }, [a]);
  // Il punto delle migliaia anche a quattro cifre: «5.664», non «5664».
  return <>{String(valore).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</>;
}
